import React, { useEffect, useState } from 'react';
import { TooltipHost } from '@fluentui/react';
import styles from './ModelIndicator.module.css';
import { buildApiUrl } from '../../config/apiConfig';

interface ModelIndicatorProps {
  model?: string;
  className?: string;
}

interface ModelOption {
  id: string;
  label: string;
}

// Cache for model display names to avoid repeated API calls
let modelDisplayNameCache: Record<string, string> = {};
let cachePromise: Promise<Record<string, string>> | null = null;

const fetchModelDisplayNames = async (): Promise<Record<string, string>> => {
  if (cachePromise) {
    return cachePromise;
  }

  cachePromise = (async () => {
    try {
      const response = await fetch(buildApiUrl('/available_models'), { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const modelOptions: ModelOption[] = data.model_options || [];
        
        // Build cache mapping model ID to display name
        const cache: Record<string, string> = {};
        modelOptions.forEach(option => {
          cache[option.id] = option.label;
        });
        
        modelDisplayNameCache = cache;
        return cache;
      } else {
        return {};
      }
    } catch (error) {
      console.warn('Failed to fetch model display names:', error);
      return {};
    }
  })();

  return cachePromise;
};

export const ModelIndicator: React.FC<ModelIndicatorProps> = ({ model, className }) => {
  const [modelDisplayName, setModelDisplayName] = useState<string>('');

  useEffect(() => {
    if (!model) {
      setModelDisplayName('');
      return;
    }

    // Check cache first
    if (modelDisplayNameCache[model]) {
      setModelDisplayName(modelDisplayNameCache[model]);
      return;
    }

    // Fetch model configurations to get display name
    const loadDisplayName = async () => {
      const cache = await fetchModelDisplayNames();
      const displayName = cache[model] || model;
      setModelDisplayName(displayName);
    };

    loadDisplayName();
  }, [model]);

  if (!model) return null;

  const displayName = modelDisplayName || model;

  return (
    <div className={`${styles.modelContainer} ${className || ''}`}>
      <TooltipHost content={displayName}>
        <div className={styles.modelIconWrapper}>
          <span className={styles.modelIcon}>
            🧠
          </span>
        </div>
      </TooltipHost>
    </div>
  );
};